<template>
  <div class="al-shell">
    <!-- Sidebar -->
    <nav class="al-sidebar" :class="{ 'al-sidebar--collapsed': collapsed }">
      <div class="al-sidebar__brand">
        <div class="al-sidebar__wordmark">pmm</div>
        <div class="al-sidebar__tagline" v-if="!collapsed">Digital Ledger</div>
      </div>

      <ul class="al-sidebar__nav">
        <li v-for="item in menuItems" :key="item.key">
          <button
            type="button"
            class="al-sidebar__link"
            :class="{ 'is-active': activeKey === item.key }"
            @click="handleMenuClick(item.key)"
          >
            <span class="material-symbols-outlined al-sidebar__icon">{{ item.icon }}</span>
            <span class="al-sidebar__label" v-if="!collapsed">{{ item.label }}</span>
          </button>
        </li>
      </ul>

      <div class="al-sidebar__footer">
        <button
          type="button"
          class="al-sidebar__link"
          :class="{ 'is-active': activeKey === 'settings' }"
          @click="handleMenuClick('settings')"
        >
          <span class="material-symbols-outlined al-sidebar__icon">settings</span>
          <span class="al-sidebar__label" v-if="!collapsed">Settings</span>
        </button>
        <button
          type="button"
          class="al-sidebar__collapse"
          @click="collapsed = !collapsed"
          :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        >
          <span class="material-symbols-outlined">{{ collapsed ? 'chevron_right' : 'chevron_left' }}</span>
        </button>
      </div>
    </nav>

    <!-- Main -->
    <div class="al-main">
      <header class="al-topbar">
        <h2 class="al-topbar__title">{{ currentPageTitle }}</h2>
        <div class="al-topbar__actions">
          <span class="al-topbar__date al-subtle al-tabular">{{ todayLabel }}</span>
          <button type="button" class="al-btn al-btn--ghost" @click="handleLogout">
            <span class="material-symbols-outlined" style="font-size: 18px">logout</span>
            <span>Logout</span>
          </button>
        </div>
      </header>

      <main class="al-content">
        <slot />
      </main>
    </div>

    <!-- Snapshot drawer (kept on naive-ui) -->
    <n-drawer
      v-model:show="showSnapshotDrawer"
      :width="502"
      :mask-closable="true"
    >
      <n-drawer-content
        title="Create Account Snapshots"
        closable
        @close="showSnapshotDrawer = false"
      >
        <SnapshotManagement @success="onSnapshotSuccess" />
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { NDrawer, NDrawerContent } from 'naive-ui';
import { useAuthStore } from '@/stores/authStore';
import SnapshotManagement from './SnapshotManagement.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const collapsed = ref(false);
const showSnapshotDrawer = ref(false);

const menuItems = [
  { key: 'dashboard', label: 'Overview', icon: 'dashboard' },
  { key: 'monthly', label: 'Monthly', icon: 'calendar_month' },
  { key: 'accounts', label: 'Accounts', icon: 'account_balance' },
  { key: 'transactions', label: 'Ledger', icon: 'receipt_long' },
  { key: 'incomes', label: 'Incomes', icon: 'payments' },
  { key: 'expenses', label: 'Expenses', icon: 'shopping_bag' },
  { key: 'loans', label: 'Loans', icon: 'request_quote' },
];

const activeKey = computed(() => (route.name ? route.name.toLowerCase() : 'dashboard'));
const currentPageTitle = computed(() => route.name || 'Overview');

const todayLabel = computed(() =>
  new Date().toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
);

const handleMenuClick = (key) => {
  router.push(`/${key}`);
};

const handleLogout = async () => {
  await authStore.logout();
  router.push('/login');
};

const onSnapshotSuccess = () => {
  showSnapshotDrawer.value = false;
};

defineExpose({ showSnapshotDrawer });
</script>

<style scoped>
.al-shell {
  display: flex;
  min-height: 100vh;
  background: var(--al-background);
  color: var(--al-on-background);
}

/* ---------- Sidebar ---------- */
.al-sidebar {
  width: 260px;
  flex-shrink: 0;
  background: var(--al-surface-container-lowest);
  display: flex;
  flex-direction: column;
  padding: 2rem 0 1.25rem;
  position: sticky;
  top: 0;
  height: 100vh;
  transition: width 0.25s ease;
}
.al-sidebar--collapsed {
  width: 80px;
}

.al-sidebar__brand {
  padding: 0 2rem 1.5rem;
}
.al-sidebar__wordmark {
  font-family: var(--al-font-headline);
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: 0.04em;
  color: var(--al-primary);
}
.al-sidebar__tagline {
  font-size: 0.75rem;
  color: rgba(228, 225, 231, 0.5);
  margin-top: 0.25rem;
  letter-spacing: 0.02em;
}

.al-sidebar__nav {
  list-style: none;
  padding: 0;
  margin: 1.5rem 0 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.al-sidebar__link {
  all: unset;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 2rem;
  color: rgba(228, 225, 231, 0.45);
  font-family: var(--al-font-headline);
  font-weight: 500;
  letter-spacing: 0.02em;
  font-size: 0.9375rem;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}
.al-sidebar__link:hover {
  color: rgba(228, 225, 231, 0.85);
  background: var(--al-surface-container);
}
.al-sidebar__link.is-active {
  color: var(--al-primary);
  background: var(--al-surface-container);
  border-left-color: var(--al-primary-container);
  font-weight: 700;
}
.al-sidebar__link.is-active .al-sidebar__icon {
  font-variation-settings: 'FILL' 1;
  color: var(--al-primary-container);
}

.al-sidebar__icon {
  font-size: 22px;
  flex-shrink: 0;
}

.al-sidebar__footer {
  padding: 0 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.al-sidebar__collapse {
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(228, 225, 231, 0.45);
  font-size: 0.8125rem;
  padding: 0.375rem 0;
  border-radius: var(--al-radius-sm);
  transition: color 0.2s ease, background-color 0.2s ease;
}
.al-sidebar__collapse:hover {
  color: var(--al-primary);
  background: var(--al-surface-container);
}

/* ---------- Main ---------- */
.al-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.al-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem;
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(19, 19, 23, 0.72);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.al-topbar__title {
  font-family: var(--al-font-headline);
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--al-on-surface);
}

.al-topbar__actions {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.al-topbar__date {
  display: none;
}
@media (min-width: 768px) {
  .al-topbar__date {
    display: inline-flex;
  }
}

.al-content {
  flex: 1;
  padding: 2rem 2.5rem 3rem;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
}

/* Mobile layout: collapse sidebar to icons */
@media (max-width: 720px) {
  .al-sidebar {
    width: 72px;
  }
  .al-sidebar__brand,
  .al-sidebar__tagline,
  .al-sidebar__label {
    display: none;
  }
  .al-content {
    padding: 1.25rem 1rem 2rem;
  }
  .al-topbar {
    padding: 0.75rem 1rem;
  }
}
</style>
